import { STATE_METADATA } from '@contracts/order/valueNormalizers/State';
import { DeliveryTypeOption } from '@src/clients/domain/Client';
import type { ClientFormSchema } from '@src/clients/schemas/ClientFormSchema';
import { Checkbox } from '@src/common/components/Checkbox';
import { FormDropdown } from '@src/common/components/FormDropdown';
import { Stack } from '@src/common/components/Stack';
import { LanguageField } from '@src/common/Language';
import { theme } from '@src/common/styles/Theme';
import { ControllerFormTextInput } from '@src/orders/PermitFormSections/ControllerFormTextInput';
import { fieldI18nKeyFrom } from '@src/orders/PermitFormSections/FieldI18nKeyFrom';
import {
  CompanyMailingAddressDetailsField,
  CompanyMailingAddressField,
  CompanyMailingCityField,
  CompanyMailingStateField,
  CompanyMailingZipCodeField,
  CompanyPhysicalAddressDetailsField,
  CompanyPhysicalAddressField,
  CompanyPhysicalCityField,
  CompanyPhysicalStateField,
  CompanyPhysicalZipCodeField,
} from '@src/permitField/CompanyAddressFields';
import {
  CarrierContactNameField,
  CompanyEmailField,
  CompanyFaxField,
  CompanyNameField,
  CompanyPhoneField,
  DoingBusinessAsField,
} from '@src/permitField/CompanyInformationSectionFields';
import {
  PrimaryDeliveryTypeField,
  PrimaryDeliveryTypeValueField,
  SecondaryDeliveryTypeField,
} from '@src/permitField/PermitDeliveryAndNotificationsFields';
import { type ChangeEvent } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

import { ClientFormSection } from './ClientFormSection';

const stateDropdownOptions = Object.entries(STATE_METADATA).map(
  ([value, { abbreviation }]) => ({
    value,
    label: abbreviation,
  }),
);

interface Props<T extends FieldValues> {
  control: Control<T>;
  mailingIsSameAsPhysical: boolean;
  onMailingIsSameAsPhysicalChange: (value: boolean) => void;
  AdditionalDetails?: (props: { control: Control<T> }) => JSX.Element;
}

export const ClientDetailsForm = ({
  control,
  mailingIsSameAsPhysical,
  onMailingIsSameAsPhysicalChange,
  AdditionalDetails,
}: Props<yup.InferType<typeof ClientFormSchema>>) => {
  const { t } = useTranslation();

  const [primaryDeliveryType, secondaryDeliveryType] = useWatch({
    control,
    name: [
      'primaryDeliveryType.primaryDeliveryType',
      'secondaryDeliveryType.secondaryDeliveryType',
      'secondaryDeliveryType.value',
    ],
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onMailingIsSameAsPhysicalChange(event.target.checked);
  };

  return (
    <Stack direction="column" gap={theme.spacing['9']}>
      <ClientFormSection title={t('editClient.information')}>
        <Stack
          direction="column"
          gap={theme.spacing['4']}
          aria-label="companyInformationSection"
        >
          <Stack direction="row" gap={theme.spacing['6']}>
            <ControllerFormTextInput
              label={t(`fields.${CompanyNameField.fieldName}`)}
              control={control}
              fieldName="name"
            />
            <ControllerFormTextInput
              control={control}
              fieldName={DoingBusinessAsField.fieldName}
            />
          </Stack>

          {AdditionalDetails && <AdditionalDetails control={control} />}
        </Stack>
      </ClientFormSection>

      <ClientFormSection title={t('editClient.contact')}>
        <Stack
          direction="column"
          gap={theme.spacing['8']}
          aria-label="companyContactSection"
        >
          <Stack direction="row" gap={theme.spacing['6']}>
            <ControllerFormTextInput
              label={t(`fields.${CarrierContactNameField.fieldName}`)}
              control={control}
              fieldName="primaryContact.name"
            />
            <ControllerFormTextInput
              label={t(`fields.${CompanyPhoneField.fieldName}`)}
              control={control}
              fieldName="primaryContact.phone"
            />
          </Stack>

          <Stack direction="row" gap={theme.spacing['6']}>
            <ControllerFormTextInput
              label={t(`fields.${CompanyEmailField.fieldName}`)}
              control={control}
              fieldName="primaryContact.email"
            />
            <ControllerFormTextInput
              label={t(`fields.${CompanyFaxField.fieldName}`)}
              control={control}
              fieldName="fax"
            />
          </Stack>
        </Stack>
      </ClientFormSection>

      <ClientFormSection title={t('editClient.address')}>
        <Stack direction="column" gap={theme.spacing['8']}>
          <ControllerFormTextInput
            label={t(`fields.${CompanyPhysicalAddressField.fieldName}`)}
            control={control}
            fieldName="address.address"
          />

          <ControllerFormTextInput
            label={t(`fields.${CompanyPhysicalAddressDetailsField.fieldName}`)}
            control={control}
            fieldName="address.addressDetails"
          />

          <Stack direction="row" gap={theme.spacing['6']}>
            <ControllerFormTextInput
              label={t(`fields.${CompanyPhysicalCityField.fieldName}`)}
              control={control}
              fieldName="address.city"
            />

            <FormDropdown
              label={t(`fields.${CompanyPhysicalStateField.fieldName}`)}
              fieldName="address.state"
              options={stateDropdownOptions}
              control={control}
            />

            <ControllerFormTextInput
              label={t(`fields.${CompanyPhysicalZipCodeField.fieldName}`)}
              control={control}
              fieldName="address.zipCode"
            />
          </Stack>
        </Stack>
      </ClientFormSection>
      <ClientFormSection title={t('editClient.mailingAddress')}>
        <Checkbox
          checked={mailingIsSameAsPhysical}
          onChange={handleChange}
          label={t('companyAddress.mailingSame')}
        />
      </ClientFormSection>
      {!mailingIsSameAsPhysical && (
        <Stack gap="12px" aria-labelledby="mailingAddressSection">
          <ControllerFormTextInput
            label={t(`fields.${CompanyMailingAddressField.fieldName}`)}
            control={control}
            fieldName="mailingAddress.address"
          />

          <ControllerFormTextInput
            label={t(`fields.${CompanyMailingAddressDetailsField.fieldName}`)}
            control={control}
            fieldName="mailingAddress.addressDetails"
          />

          <Stack direction="row" gap="12px">
            <ControllerFormTextInput
              label={t(`fields.${CompanyMailingCityField.fieldName}`)}
              control={control}
              fieldName="mailingAddress.city"
            />

            <FormDropdown
              label={t(`fields.${CompanyMailingStateField.fieldName}`)}
              fieldName="mailingAddress.state"
              options={stateDropdownOptions}
              control={control}
            />

            <ControllerFormTextInput
              label={t(`fields.${CompanyMailingZipCodeField.fieldName}`)}
              control={control}
              fieldName="mailingAddress.zipCode"
            />
          </Stack>
        </Stack>
      )}

      <ClientFormSection title={t('editClient.communicationLanguage')}>
        <Stack direction="row" gap={theme.spacing['8']}>
          <FormDropdown
            control={control}
            fieldName="communicationLanguage"
            label={t('fields.communicationLanguage.label')}
            options={LanguageField.options.map((option) => ({
              value: option,
              label: t(`language.${option}`),
            }))}
          />
        </Stack>
      </ClientFormSection>

      <ClientFormSection title={t('editClient.permitDeliveryAndNotifications')}>
        <Stack gap={theme.spacing['8']}>
          <Stack direction="row" gap={theme.spacing['6']}>
            <FormDropdown
              control={control}
              fieldName="primaryDeliveryType.primaryDeliveryType"
              label={t('fields.primaryDeliveryType')}
              options={PrimaryDeliveryTypeField.options.map((option) => ({
                value: option,
                label: t(`deliveryType.${option}`),
              }))}
            />

            {primaryDeliveryType &&
              primaryDeliveryType !== DeliveryTypeOption.WEB_DOWNLOAD && (
                <ControllerFormTextInput
                  label={t(
                    `fields.${fieldI18nKeyFrom(
                      PrimaryDeliveryTypeValueField.fieldName,
                      primaryDeliveryType as DeliveryTypeOption,
                    )}`,
                  )}
                  control={control}
                  fieldName="primaryDeliveryType.value"
                />
              )}
          </Stack>

          <Stack direction="row" gap={theme.spacing['6']}>
            <FormDropdown
              control={control}
              fieldName="secondaryDeliveryType.secondaryDeliveryType"
              label={t('fields.secondaryDeliveryType')}
              options={SecondaryDeliveryTypeField.options.map((option) => ({
                value: option,
                label: t(`deliveryType.${option}`),
              }))}
            />

            {secondaryDeliveryType &&
              secondaryDeliveryType !== DeliveryTypeOption.WEB_DOWNLOAD && (
                <ControllerFormTextInput
                  label={t(
                    `fields.${fieldI18nKeyFrom(
                      PrimaryDeliveryTypeValueField.fieldName,
                      secondaryDeliveryType as DeliveryTypeOption,
                    )}`,
                  )}
                  control={control}
                  fieldName="secondaryDeliveryType.value"
                />
              )}
          </Stack>
        </Stack>
      </ClientFormSection>

      <ClientFormSection title={t('editClient.notification')}>
        <Stack direction="row" gap={theme.spacing['6']}>
          <ControllerFormTextInput
            control={control}
            label={t('fields.notificationEmailAddress')}
            fieldName="permitNotifications.email"
          />
          <ControllerFormTextInput
            control={control}
            label={t('fields.notificationMobilePhone')}
            fieldName="permitNotifications.phone"
          />
        </Stack>
      </ClientFormSection>
    </Stack>
  );
};
